import type {
  JobChainTemplateListParams,
  JobChainTemplateStatus,
  JobChainTemplateScope,
} from '@/api/JobChainTemplates';

export type JobChainTemplateUrlState = JobChainTemplateListParams;

export type JobChainTemplateBusinessFilters = Partial<
  Pick<
    JobChainTemplateUrlState,
    'scope' | 'customerId' | 'jobChainType' | 'status' | 'version'
  >
>;

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

const validScopes: JobChainTemplateScope[] = ['Global', 'Customer'];
const validStatuss: JobChainTemplateStatus[] = ['Draft', 'Active', 'Inactive'];

const parsePositiveInteger = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && Number.isInteger(parsed) && parsed > 0
    ? parsed
    : undefined;
};

const isScope = (value: string | null): value is JobChainTemplateScope =>
  value !== null && validScopes.includes(value as JobChainTemplateScope);

const isStatus = (value: string | null): value is JobChainTemplateStatus =>
  value !== null && validStatuss.includes(value as JobChainTemplateStatus);

const normalizeState = (
  value: Partial<JobChainTemplateUrlState>,
): JobChainTemplateUrlState => {
  const scope = validScopes.includes(value.scope as JobChainTemplateScope)
    ? value.scope
    : undefined;
  const status = validStatuss.includes(value.status as JobChainTemplateStatus)
    ? value.status
    : undefined;
  const customerId =
    scope === 'Customer' &&
    Number.isFinite(value.customerId) &&
    Number.isInteger(value.customerId) &&
    (value.customerId ?? 0) > 0
      ? value.customerId
      : undefined;
  const version =
    Number.isFinite(value.version) &&
    Number.isInteger(value.version) &&
    (value.version ?? 0) > 0
      ? value.version
      : undefined;
  const jobChainType = value.jobChainType?.trim() || undefined;
  const page =
    Number.isFinite(value.page) &&
    Number.isInteger(value.page) &&
    (value.page ?? 0) > 0
      ? value.page!
      : DEFAULT_PAGE;
  const pageSize =
    Number.isFinite(value.pageSize) &&
    Number.isInteger(value.pageSize) &&
    (value.pageSize ?? 0) > 0
      ? value.pageSize!
      : DEFAULT_PAGE_SIZE;

  return {
    ...(scope ? { scope } : {}),
    ...(customerId ? { customerId } : {}),
    ...(jobChainType ? { jobChainType } : {}),
    ...(status ? { status } : {}),
    ...(version ? { version } : {}),
    page,
    pageSize,
  };
};

export const parseJobChainTemplateUrlState = (
  input: string | URLSearchParams,
): JobChainTemplateUrlState => {
  const searchParams =
    typeof input === 'string'
      ? new URLSearchParams(input.startsWith('?') ? input.slice(1) : input)
      : input;
  const scopeValue = searchParams.get('scope');
  const statusValue = searchParams.get('status');
  const jobChainType = searchParams.get('jobChainType')?.trim() || undefined;
  const scope = isScope(scopeValue) ? scopeValue : undefined;
  const status = isStatus(statusValue) ? statusValue : undefined;

  return normalizeState({
    scope,
    customerId: parsePositiveInteger(searchParams.get('customerId')),
    jobChainType,
    status,
    version: parsePositiveInteger(searchParams.get('version')),
    page: parsePositiveInteger(searchParams.get('page')),
    pageSize: parsePositiveInteger(searchParams.get('pageSize')),
  });
};

export const setJobChainTemplateFilter = (
  current: JobChainTemplateUrlState,
  patch: JobChainTemplateBusinessFilters,
): JobChainTemplateUrlState =>
  normalizeState({ ...current, ...patch, page: DEFAULT_PAGE });

export const clearJobChainTemplateFilters = (
  current: JobChainTemplateUrlState,
): JobChainTemplateUrlState =>
  normalizeState({ page: DEFAULT_PAGE, pageSize: current.pageSize });

export const setJobChainTemplatePagination = (
  current: JobChainTemplateUrlState,
  page: number,
  pageSize: number,
): JobChainTemplateUrlState =>
  normalizeState({
    ...current,
    page: pageSize === current.pageSize ? page : DEFAULT_PAGE,
    pageSize,
  });

export const serializeJobChainTemplateUrlState = (
  state: JobChainTemplateUrlState,
): URLSearchParams => {
  const normalized = normalizeState(state);
  const searchParams = new URLSearchParams();

  if (normalized.scope) searchParams.set('scope', normalized.scope);
  if (normalized.customerId)
    searchParams.set('customerId', String(normalized.customerId));
  if (normalized.jobChainType)
    searchParams.set('jobChainType', normalized.jobChainType);
  if (normalized.status) searchParams.set('status', normalized.status);
  if (normalized.version)
    searchParams.set('version', String(normalized.version));
  searchParams.set('page', String(normalized.page));
  searchParams.set('pageSize', String(normalized.pageSize));

  return searchParams;
};

export const hasJobChainTemplateBusinessFilters = (
  state: JobChainTemplateUrlState,
) =>
  Boolean(
    state.scope ||
      state.customerId ||
      state.jobChainType ||
      state.status ||
      state.version,
  );
